import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import axios from "axios";
import { i18n } from "@/i18n";
const { t } = i18n.global;
import store from "@/store";
import { jwtDecode } from "jwt-decode";
import { hardLogout } from "@/api";
import * as sessionTokens from "@/auth/sessionTokens";

export const UPDATE_ACTION_STORAGE_KEY = "app:update-action";

function getLastSeenUpdateStorageKeyForUserId(userId) {
    return `app:last-seen-update:${userId}`;
}

/**
 * Parse an ISO-8601 deploy timestamp to epoch milliseconds for ordering.
 * Mixed suffixes (Z vs +00:00) parse to the same instant; string compare does not.
 * @param {string|null|undefined} isoTimestamp
 * @returns {number}
 */
function parseDeployTimestampToMs(isoTimestamp) {
    if (!isoTimestamp || typeof isoTimestamp !== "string") return 0;
    const epochMilliseconds = Date.parse(isoTimestamp);
    return Number.isFinite(epochMilliseconds) ? epochMilliseconds : 0;
}

/**
 * Latest "already seen" instant from session + localStorage markers (whichever is newer).
 * @param {...(string|null|undefined)} isoTimestamps
 * @returns {number}
 */
function maxSeenTimestampMs(...isoTimestamps) {
    let latestMilliseconds = 0;
    for (const candidate of isoTimestamps) {
        const candidateMilliseconds = parseDeployTimestampToMs(candidate);
        if (candidateMilliseconds > latestMilliseconds) {
            latestMilliseconds = candidateMilliseconds;
        }
    }
    return latestMilliseconds;
}

function broadcastUpdateAction(action, timestamp, userId) {
    localStorage.setItem(
        UPDATE_ACTION_STORAGE_KEY,
        JSON.stringify({
            action,
            timestamp,
            userId,
            emittedAt: Date.now(),
        })
    );
}

/**
 * @param {{ userId?: number|string }} payload - Parsed payload from {@link UPDATE_ACTION_STORAGE_KEY}.
 * @returns {boolean} True if this tab should run the action (same user as broadcaster, or legacy payload with no userId).
 */
export function shouldApplyCrossTabUpdatePayload(payload) {
    if (!payload || typeof payload !== "object") return false;
    if (!Object.prototype.hasOwnProperty.call(payload, "userId")) return true;
    const tabUserId = JSON.parse(sessionStorage.getItem("user") || "{}")?.id;
    if (tabUserId === undefined || tabUserId === null || tabUserId === "") {
        return false;
    }
    return String(payload.userId) === String(tabUserId);
}

/**
 * Apply an app update action and keep tab-local update marker in sync.
 *
 * @param {"minor-reload"|"major-logout"} action - Action to execute.
 * @param {string} timestamp - Backend update timestamp.
 * @returns {void}
 */
export function applyUpdateAction(action, timestamp) {
    if (timestamp) {
        const user = JSON.parse(sessionStorage.getItem("user") || "{}");
        user.last_seen_update = timestamp;
        sessionStorage.setItem("user", JSON.stringify(user));
        if (user?.id !== undefined && user?.id !== null && user?.id !== "") {
            localStorage.setItem(
                getLastSeenUpdateStorageKeyForUserId(user.id),
                timestamp
            );
        }
    }

    if (action === "minor-reload") {
        window.location.reload();
        return;
    }

    if (action === "major-logout") {
        sessionTokens.clearSessionTokens();
        sessionTokens.clearLegacyJwtLocalStorage();
        delete axios.defaults.headers.common["Authorization"];
        sessionStorage.clear();
        const base = (process.env.BASE_URL || "/").replace(/\/$/, "") || "";
        window.location = `${base}/login?update=1`;
    }
}

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Formats a UTC datetime string into local time.
 *
 * @param {string} utcTimeString - The UTC timestamp from the backend.
 * @param {string} [format="DD.MM.YYYY - HH:mm"] - Optional format string using dayjs tokens.
 * @returns {string} - The formatted local time string.
 *
 * Example: "2025-02-18T06:22:12.587530Z" -> "18.02.2025 - 08:22" (if user's local time is GMT+2)
 */
export function formatLocalTime(utcTimeString, format = "DD.MM.YYYY - HH:mm") {
    if (!utcTimeString) return "Unknown";

    // Detect the user's time zone from the browser
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Convert the UTC time to the user's local time zone and format it
    return dayjs.utc(utcTimeString).tz(userTimeZone).format(format);
}

/**
 * Format an ISO date string for display as DD.MM.YYYY (user's local time).
 * Use for system timestamps (created_at, etc.).
 */
export function formatDisplayDate(isoString) {
    if (!isoString) return "";
    return formatLocalTime(isoString, "DD.MM.YYYY");
}

/**
 * Format an ISO date string for display as HH:mm (time only).
 * @param {string} isoString - UTC/ISO date string from backend.
 * @returns {string} Formatted time in local timezone.
 */
export function formatDisplayTime(isoString) {
    if (!isoString) return "";
    return formatLocalTime(isoString, "HH:mm");
}

/**
 * Format a date string as DD.MM.YYYY without timezone conversion.
 * Use for location-based dates (entry allowed windows) where date is implied by location.
 * @param {string} dateStr - ISO date "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss..."
 * @returns {string} "DD.MM.YYYY"
 */
export function formatDateOnly(dateStr) {
    if (!dateStr) return "";
    const datePart = String(dateStr).split("T")[0];
    if (!datePart || datePart.length < 10) return "";
    const [y, m, d] = datePart.split("-");
    return `${d.padStart(2, "0")}.${m.padStart(2, "0")}.${y}`;
}

/**
 * Format an ISO date string for display as DD.MM.YYYY HH:mm.
 * @param {string} isoString - UTC/ISO date string from backend.
 * @returns {string} Formatted datetime in local time.
 */
export function formatDisplayDateTime(isoString) {
    if (!isoString) return "";
    return formatLocalTime(isoString, "DD.MM.YYYY HH:mm");
}

/**
 * Format a date range for display. Same date → single date; different dates → "dateA - dateB".
 * Uses formatDateOnly (no timezone conversion) for location-based entry dates.
 * @param {string} dateStringA - Start date (ISO).
 * @param {string} dateStringB - End date (ISO).
 * @returns {string} Formatted range.
 */
export function formatDateRange(dateStringA, dateStringB) {
    if (!dateStringA || !dateStringB) return "";
    const a = formatDateOnly(dateStringA);
    const b = formatDateOnly(dateStringB);
    return a === b ? a : `${a} - ${b}`;
}

/**
 * Normalizes a date value to YYYY-MM-DD for form/API use.
 * @param {Date|string} dateValue - Date object or ISO/date string from backend.
 * @returns {string|null} - "YYYY-MM-DD" or null if invalid.
 */
export function formatToYmd(dateValue) {
    if (!dateValue) return null;
    const dateObject = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (isNaN(dateObject.getTime())) return null;
    const year = dateObject.getFullYear();
    const month = String(dateObject.getMonth() + 1).padStart(2, "0");
    const dayOfMonth = String(dateObject.getDate()).padStart(2, "0");
    return `${year}-${month}-${dayOfMonth}`;
}

/**
 * Calendar-day min/max bounds around an anchor date (YYYY-MM-DD or ISO prefix).
 * Uses local noon on the anchor to reduce DST surprises when stepping by whole days.
 *
 * @param {string|null|undefined} anchorIsoDate - Ten-character ISO date prefix preferred.
 * @param {number|string|null|undefined} toleranceDays - Half-width in calendar days (inclusive span).
 * @returns {{ minDateYmd: string|null, maxDateYmd: string|null }}
 */
export function calendarToleranceBoundsAroundAnchorIso(anchorIsoDate, toleranceDays) {
    const prefix =
        typeof anchorIsoDate === "string" && anchorIsoDate.length >= 10 ? anchorIsoDate.slice(0, 10) : null;
    if (!prefix) {
        return { minDateYmd: null, maxDateYmd: null };
    }
    const anchorDate = new Date(`${prefix}T12:00:00`);
    if (Number.isNaN(anchorDate.getTime())) {
        return { minDateYmd: null, maxDateYmd: null };
    }
    const parsedTolerance = Number.parseInt(String(toleranceDays), 10);
    const safeTolerance =
        Number.isFinite(parsedTolerance) && parsedTolerance >= 0 ? parsedTolerance : 0;
    const millisecondsPerDay = 86400000;
    const earliestDate = new Date(anchorDate.getTime() - safeTolerance * millisecondsPerDay);
    const latestDate = new Date(anchorDate.getTime() + safeTolerance * millisecondsPerDay);
    return {
        minDateYmd: formatToYmd(earliestDate),
        maxDateYmd: formatToYmd(latestDate),
    };
}

/**
 * Normalizes a time value to HH:mm for form constraints.
 * @param {string|object} timeValue - Backend time string (e.g. "12:30:00") or object.
 * @returns {string|null} - "HH:mm" or null if invalid.
 */
export function formatToHhMm(timeValue) {
    if (!timeValue) return null;
    if (typeof timeValue === "string") return timeValue.substring(0, 5);
    return timeValue ? `${String(timeValue).padStart(2, "0")}:00` : null;
}

/**
 * Converts HH:mm or HH:mm:ss to minutes since midnight.
 * @param {string} timeStr - Time string (e.g. "12:30" or "12:30:00").
 * @returns {number|null} - Minutes or null if invalid.
 */
export function timeToMinutes(timeStr) {
    if (!timeStr) return null;
    const parts = String(timeStr).split(":").map(Number);
    const h = parts[0];
    const m = parts[1];
    if (h == null || isNaN(h) || m == null || isNaN(m)) return null;
    return h * 60 + m;
}

/**
 * Normalize a price-like input into an integer number for API usage.
 * - Returns null for empty/invalid values.
 * - Enforces integers only (no decimals) and > 0.
 *
 * Used by reservation offer / counter-offer inputs to match the existing
 * "cleanInfo" pattern used during entry publish.
 *
 * @param {string|number|null|undefined} value
 * @returns {number|null}
 */
export function cleanPriceInput(value) {
    if (value == null) return null;
    const rawInputText = String(value).trim();
    if (!rawInputText) return null;
    const parsedNumber = Number.parseFloat(rawInputText);
    if (!Number.isFinite(parsedNumber)) return null;
    return parsedNumber;
}

/**
 * Transforms the API response (with snake_case keys)
 * into a structure that matches your form (with camelCase keys).
 * Adjust this function as needed for both truck and ware types.
 */
export function transformEntryData(apiData) {
    return {
        uid: apiData.uid,
        formType: apiData.form_type,
        loadingLocation: apiData.loading_location
            ? {
                  id: apiData.loading_location.id,
                  postalCode: apiData.loading_location.postal_code,
                  dateStart: apiData.loading_location.date_start,
                  dateEnd: apiData.loading_location.date_end,
                  date: apiData.loading_location.date?.map(
                      (dateStr) => new Date(dateStr)
                  ) || [],
                  timeStart: formatToHhMm(apiData.loading_location.time_start),
                  timeEnd: formatToHhMm(apiData.loading_location.time_end),
                  country_code: apiData.loading_location.country_code,
                  place_id: apiData.loading_location.place_id,
                  place_display_name: apiData.loading_location.place_display_name,
              }
            : null,
        unloadingLocation: apiData.unloading_location
            ? {
                  id: apiData.unloading_location.id,
                  postalCode: apiData.unloading_location.postal_code,
                  dateStart: apiData.unloading_location.date_start,
                  dateEnd: apiData.unloading_location.date_end,
                  date: apiData.unloading_location.date?.map(
                      (dateStr) => new Date(dateStr)
                  ) || [],
                  // Backend can return "HH:mm:ss"; normalize to "HH:mm" for form display/constraints.
                  timeStart: formatToHhMm(apiData.unloading_location.time_start),
                  timeEnd: formatToHhMm(apiData.unloading_location.time_end),
                  country_code: apiData.unloading_location.country_code,
                  place_id: apiData.unloading_location.place_id,
                  place_display_name: apiData.unloading_location.place_display_name,
              }
            : null,
        info: apiData.info
            ? {
                  id: apiData.info.id,
                  weight: apiData.info.weight.toString(),
                  volume: apiData.info.volume,
                  mdp: apiData.info.mdp.toString(),
                  price: apiData.info.price,
                  paymentTerm: apiData.info.payment_term,
                  currency: apiData.info.currency,
                  vatType: apiData.info.vat_type,
              }
            : null,
        transportOptions: apiData.transport_options
            ? apiData.form_type === "truck"
                ? {
                      id: apiData.transport_options.id,
                      truckTransportType:
                          apiData.transport_options.truck_transport_type,
                      truckSpecialFeatures:
                          apiData.transport_options.truck_special_features,
                      truckCoating: apiData.transport_options.truck_coating,
                      truckType: apiData.transport_options.truck_type,
                  }
                : {
                      id: apiData.transport_options.id,
                      truckCoating: apiData.transport_options.truck_coating,
                      platformType: apiData.transport_options.platform_type,
                      escrowAccount: apiData.transport_options.escrow_account,
                      dangerousLoad: apiData.transport_options.dangerous_load,
                      truckSpecialFeatures:
                          apiData.transport_options.truck_special_features,
                      truckTransportType:
                          apiData.transport_options.truck_transport_type,
                  }
            : null,
        notes: apiData.notes,
        created_at: apiData.created_at,
        user: apiData.user,
    };
}

/**
 * Finds the corresponding text for a given enum value.
 * @param {Object} enumObj - The ENUM object to search in.
 * @param {Number} value - The value received from the backend.
 * @returns {String} - The corresponding text value.
 */
export function mapEnumValue(enumObj, value) {
    if (value === null || value === undefined) return "Unknown";

    const numericValue = Number(value);

    if (Array.isArray(enumObj)) {
        const entry = enumObj.find((item) => item.value === numericValue);
        return entry ? entry.text : "Unknown";
    } else {
        const entry = Object.values(enumObj).find(
            (item) => item.value === numericValue
        );
        return entry ? entry.text : "Unknown";
    }
}

/**
 * Call after sessionStorage `user` and JWT are set. Requires `user.id` (login
 * responses always include it). If the server records a newer deploy than this
 * client, PATCH ack and bump stored last_seen without a dialog so returning users
 * are not prompted right after login; existing tabs still prompt via
 * {@link startUpdateWatcher} until they reload or log out.
 */
export async function syncLastSeenDeployAfterLogin() {
    const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");
    const userId = sessionUser?.id;
    if (userId === undefined || userId === null || userId === "") return;

    try {
        const response = await axios.get(
            `${process.env.VUE_APP_API_URL}/update-info/`
        );
        const latestUpdate = Array.isArray(response.data)
            ? response.data[0]
            : response.data;
        if (!latestUpdate?.timestamp) return;

        const seenInSession = sessionUser.last_seen_update || "";
        const seenInLocalStorage =
            localStorage.getItem(getLastSeenUpdateStorageKeyForUserId(userId)) ||
            "";
        const latestSeenClientMs = maxSeenTimestampMs(
            seenInSession,
            seenInLocalStorage
        );
        const latestDeployServerMs = parseDeployTimestampToMs(
            latestUpdate.timestamp
        );
        if (latestDeployServerMs <= latestSeenClientMs) return;

        await axios.patch(
            `${process.env.VUE_APP_API_URL}/update-info/ack/`
        );
        const mergedUser = { ...sessionUser, last_seen_update: latestUpdate.timestamp };
        sessionStorage.setItem("user", JSON.stringify(mergedUser));
        localStorage.setItem(
            getLastSeenUpdateStorageKeyForUserId(userId),
            latestUpdate.timestamp
        );
    } catch (err) {
        console.error("syncLastSeenDeployAfterLogin failed", err);
    }
}

export function startUpdateWatcher() {
    const TEN_MINUTES_MS = 10 * 60 * 1_000;

    async function check() {
        try {
            // Only check for updates when the user is logged in.
            const accessToken = sessionTokens.getAccessToken();
            if (!accessToken) return;

            const response = await axios.get(
                `${process.env.VUE_APP_API_URL}/update-info/`
            );
            const latestUpdate = Array.isArray(response.data)
                ? response.data[0]
                : response.data;

            if (!latestUpdate || !latestUpdate.timestamp) return;

            const sessionUser = JSON.parse(
                sessionStorage.getItem("user") || "{}"
            );
            const userId = sessionUser?.id;
            if (userId === undefined || userId === null || userId === "") {
                return;
            }

            const seenInSession = sessionUser.last_seen_update || "";
            const seenInLocalStorage =
                localStorage.getItem(
                    getLastSeenUpdateStorageKeyForUserId(userId)
                ) || "";
            const latestSeenClientMs = maxSeenTimestampMs(
                seenInSession,
                seenInLocalStorage
            );
            const latestDeployServerMs = parseDeployTimestampToMs(
                latestUpdate.timestamp
            );
            if (latestDeployServerMs <= latestSeenClientMs) return;

            const acknowledgeDeployAndPersistSeenTime = async () => {
                await axios.patch(
                    `${process.env.VUE_APP_API_URL}/update-info/ack/`
                );
                const mergedUser = {
                    ...sessionUser,
                    last_seen_update: latestUpdate.timestamp,
                };
                sessionStorage.setItem("user", JSON.stringify(mergedUser));
                localStorage.setItem(
                    getLastSeenUpdateStorageKeyForUserId(userId),
                    latestUpdate.timestamp
                );
            };

            if (latestUpdate.type === "minor") {
                if (confirm(t("updates.minorPrompt"))) {
                    await acknowledgeDeployAndPersistSeenTime();
                    broadcastUpdateAction(
                        "minor-reload",
                        latestUpdate.timestamp,
                        userId
                    );
                    applyUpdateAction("minor-reload", latestUpdate.timestamp);
                }
            } else {
                if (confirm(t("updates.majorPrompt"))) {
                    await acknowledgeDeployAndPersistSeenTime();
                    broadcastUpdateAction(
                        "major-logout",
                        latestUpdate.timestamp,
                        userId
                    );
                    applyUpdateAction("major-logout", latestUpdate.timestamp);
                }
            }
        } catch (err) {
            console.error("update check failed", err);
            showAlertModal(store, "Update check failed", "danger", 12000, err);
        }
    }

    check();
    return setInterval(check, TEN_MINUTES_MS);
}

/**
 * Map reservation create error codes (from backend) to locale keys.
 * Returns the locale key for known codes, or null for unknown (caller uses fallback).
 */
export const RESERVATION_CREATE_ERROR_KEYS = {
    no_company: "reservation.errors.noCompany",
    entry_not_found: "reservation.errors.entryNotFound",
    ware_already_reserved: "reservation.errors.wareAlreadyReserved",
    express_uniqueness_conflict: "reservations.expressUniquenessError",
    truck_full: "reservation.errors.truckFull",
    own_entry: "reservation.errors.ownEntry",
    opening_price_required: "reservation.errors.openingPriceRequired",
    price_offer_currency_required: "reservation.errors.priceOfferCurrencyRequired",
    price_offer_vat_type_required: "reservation.errors.priceOfferVatTypeRequired",
};

/**
 * Get locale key for reservation create error. Returns key if known, else null.
 * @param {string} errorCode - Backend error code from response.data.error
 * @returns {string|null} - Locale key or null
 */
export function getReservationCreateErrorKey(errorCode) {
    if (!errorCode || typeof errorCode !== "string") return null;
    return RESERVATION_CREATE_ERROR_KEYS[errorCode] ?? null;
}

/**
 * Triggers an alert modal in the application.
 * This function commits a series of mutations to the Vuex store to configure and show an alert modal.
 * @param {Object} context - The Vuex context object, which provides access to store properties and commit mutations.
 * @param {string} message - The message to be displayed in the alert modal.
 * @param {string} type - The type of the alert (e.g., 'success', 'error', 'warning').
 * @param {number} duration - The duration in milliseconds for which the alert should be displayed.
 * @param errorDetails
 */
export function showAlertModal(
    context,
    message,
    type,
    duration,
    errorDetails = ""
) {
    if (type === "danger") {
        // Clear any existing alert and queue
        context.commit("clearCurrentAlert");
        context.dispatch("configureAlert", {
            message,
            type,
            duration,
            visible: true,
            errorDetails,
        });
    } else {
        const currentAlertType = context.state.alert.type;
        if (currentAlertType === "danger" && context.state.alert.show) {
            // Queue the alert if a danger alert is currently being displayed
            context.commit("queueAlert", {
                message,
                type,
                duration,
                errorDetails,
            });
        } else {
            // Show the alert immediately
            context.dispatch("configureAlert", {
                message,
                type,
                duration,
                visible: true,
                errorDetails,
            });
        }
    }
}

/**
 * Get the exact end datetime of an entry (loading_location.date_end + time_end).
 * @param {Object} entry - Truck or ware entry with loading_location
 * @returns {Date} - End datetime
 */
export function getEntryEndDate(entry) {
    const end = new Date(entry.loading_location.date_end);
    if (entry.loading_location?.time_end) {
        const [h, m] = entry.loading_location.time_end.split(":").map(Number);
        end.setHours(h, m, 0, 0);
    }
    return end;
}

/**
 * Check if entry expires within the given window (hours from now).
 * @param {Object} entry - Truck or ware entry
 * @param {number} hours - Window in hours (e.g. 4, 6, 24)
 * @returns {boolean}
 */
export function entryExpiresWithin(entry, hours) {
    const now = new Date();
    const limit = new Date(now.getTime() + hours * 60 * 60 * 1000);
    const end = getEntryEndDate(entry);
    return end > now && end <= limit;
}

/**
 * Remaining time until the rating window closes (API field `rating_window_expires_at`, ISO 8601).
 * Backend: 72 hours after the rater confirmed completion (see `rating_window.py`).
 *
 * @param {string|null|undefined} isoDeadline
 * @returns {null} no ISO deadline (treated as no tooltip text in the reservation rating UI)
 * @returns {{ expired: true }} deadline passed
 * @returns {{ expired: false, hours: number, minutes: number }}
 */
export function getRatingWindowRemainingParts(isoDeadline) {
    if (!isoDeadline) return null;
    const deadline = dayjs(isoDeadline);
    const now = dayjs();
    const millisecondsUntilDeadline = deadline.valueOf() - now.valueOf();
    if (millisecondsUntilDeadline <= 0) return { expired: true };
    const totalMinutes = Math.floor(millisecondsUntilDeadline / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { expired: false, hours, minutes };
}

const ONE_HOUR = 60 * 60 * 1_000; // 1 hour
const WARN_LEAD = ONE_HOUR;

function msUntilExpiry(token) {
    if (!token) return 0;
    try {
        const { exp } = jwtDecode(token); // exp is in *seconds*
        return Math.max(0, exp * 1000 - Date.now());
    } catch {
        return 0;
    } // malformed → expired
}

export function startSessionWatcher() {
    // single, *real* check function
    const check = async () => {
        const refresh = sessionTokens.getRefreshToken();
        if (!refresh) return; // no token yet / already logged-out

        const remaining = msUntilExpiry(refresh);

        /* ignore the very first tick while the token is still being set */
        if (remaining !== 0 && remaining <= WARN_LEAD) {
            showAlertModal(
                store,
                i18n.global.t("tokenExpired"),
                "warning",
                8000
            );
            hardLogout();
        }
    };

    /* defer first run so login can finish writing the token */
    setTimeout(check, 0); // run once, right after login
    return setInterval(check, ONE_HOUR); // then every hour
}